/* EZFIT nordic — cart drawer + search overlay. Generic theme feature: no store name, no
   colour, no copy is hardcoded here — every string ships from Liquid via data-* attributes and
   the sections/cart-drawer.liquid + sections/search-overlay.liquid markup.

   Vanilla, no framework, one event-delegation listener per event type on `document` (works for
   markup that is swapped/re-rendered, per lessons-shop-builder's snippet-render lesson). Every
   trigger keeps a working plain href/form action, so with this script absent or failing: the
   header cart icon links to /cart, the header search icon links to /search, and the PDP form
   posts to /cart/add normally. /cart and /search (main-cart.liquid, main-search.liquid) are the
   real no-JS pages and are unchanged.

   Mechanism (open/close, scroll-lock, focus-trap, the one-POST section-swap pattern) is reused
   from the drawers.js of an earlier store of the operator — read in full 2026-09-21 (see
   build-log-drawers.md). Renamed to store-agnostic names; Norwegian copy and
   NOK were never copied in, since every string here is a data-* attribute or a value the fetch
   itself returns.

   bundle.js (the existing tier-quantity add-to-cart script) posts its own /cart/add call for a
   2/3-item pick. To avoid a double add, it does NOT go through onCartAddSubmit below — it calls
   window.CartDrawer.swapAndOpen() itself once ITS OWN fetch succeeds. See assets/bundle.js.
*/
(function () {
  var openRoot = null;
  var openTrigger = null;
  var searchDebounce = null;
  var searchAbort = null;
  var lockedScrollY = 0;

  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('input', onInput);
  document.addEventListener('change', onChange);
  document.addEventListener('keydown', onKeydown);

  function onClick(event) {
    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      closeDialog(closer.closest('[data-cart-drawer], [data-search-overlay]'));
      return;
    }
    var cartOpener = event.target.closest('[data-cart-open]');
    if (cartOpener) {
      event.preventDefault();
      openCartDrawer(cartOpener);
      return;
    }
    var searchOpener = event.target.closest('[data-search-open]');
    if (searchOpener) {
      event.preventDefault();
      openSearchOverlay(searchOpener);
      return;
    }
    var qtyButton = event.target.closest('[data-cart-qty]');
    if (qtyButton) onQtyButton(qtyButton);
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || typeof form.matches !== 'function') return;
    if (form.matches('[data-search-overlay-form]')) {
      onSearchSubmit(event, form);
      return;
    }
    if (form.matches('form[action$="/cart/add"]')) {
      onCartAddSubmit(event, form);
    }
  }

  function onInput(event) {
    var input = event.target;
    if (input.matches && input.matches('[data-search-input]')) queueSearch(input.value);
  }

  function onChange(event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-cart-qty-input]')) return;
    var line = input.closest('[data-cart-line]');
    if (!line) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    changeCartLine(line.getAttribute('data-cart-line'), qty);
  }

  function onKeydown(event) {
    if (!openRoot) return;
    if (event.key === 'Escape') {
      closeDialog(openRoot);
      return;
    }
    if (event.key === 'Tab') trapFocus(event);
  }

  /* ---------- open/close, shared by the cart drawer and the search overlay ---------- */

  function openDialog(root, trigger) {
    if (!root) return;
    if (openRoot && openRoot !== root) closeDialog(openRoot);
    root.removeAttribute('hidden');
    root.offsetWidth; /* force reflow so the transform/opacity transition actually runs */
    root.classList.add('is-open');

    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('has-dialog');
    document.body.style.paddingRight = scrollbarWidth + 'px';
    if (!document.body.classList.contains('has-dialog-body')) {
      lockedScrollY = window.scrollY;
      document.body.classList.add('has-dialog-body');
      document.body.style.top = -lockedScrollY + 'px';
    }

    openRoot = root;
    openTrigger = trigger || null;
    if (trigger && trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-expanded', 'true');
  }

  function closeDialog(root) {
    if (!root || root.hasAttribute('hidden')) return;
    var wasOpenRoot = root === openRoot;
    var trigger = wasOpenRoot ? openTrigger : null;
    root.classList.remove('is-open');
    if (trigger && trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-expanded', 'false');

    var panel = root.querySelector('[role="dialog"]');
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      if (panel) panel.removeEventListener('transitionend', onPanelEnd);
      if (root.classList.contains('is-open')) return; /* re-opened before the transition ended */
      root.setAttribute('hidden', '');
      document.documentElement.classList.remove('has-dialog');
      document.body.style.paddingRight = '';
      if (!openRoot && document.body.classList.contains('has-dialog-body')) {
        document.body.classList.remove('has-dialog-body');
        document.body.style.top = '';
        window.scrollTo(0, lockedScrollY);
      }
      var target = trigger;
      if (target && !document.contains(target)) {
        target = target.hasAttribute('data-cart-open')
          ? document.querySelector('[data-cart-open]')
          : document.querySelector('[data-search-open]');
      }
      if (target) target.focus({ preventScroll: true });
    }
    function onPanelEnd(event) {
      if (event.target === panel) finish();
    }
    if (panel) panel.addEventListener('transitionend', onPanelEnd);
    /* prefers-reduced-motion sets every transition-duration to 0.01ms (theme.css), so
       transitionend still fires — this timeout is only a fallback if it does not. */
    setTimeout(finish, 300);
    if (wasOpenRoot) {
      openRoot = null;
      openTrigger = null;
    }
  }

  function trapFocus(event) {
    var focusable = getFocusable(openRoot);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function getFocusable(root) {
    var nodes = root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var visible = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetWidth > 0 || nodes[i].offsetHeight > 0) visible.push(nodes[i]);
    }
    return visible;
  }

  /* ---------- cart drawer ---------- */

  function openCartDrawer(trigger) {
    var root = document.querySelector('[data-cart-drawer]');
    if (!root) return;
    openDialog(root, trigger);
    var panel = root.querySelector('[role="dialog"]');
    if (panel) panel.focus();
  }

  /* Kleine publieke haak zodat bundle.js (dat zijn eigen /cart/add-aanroep doet voor een
     2/3-pick) de lade kan openen in plaats van door te sturen naar /cart. Zonder lade in de
     DOM geeft exists() false en valt bundle.js terug op de gewone doorverwijzing. */
  window.themeCartDrawer = {
    exists: function () {
      return !!document.querySelector('[data-cart-drawer]');
    },
    openWithSections: function (sections, trigger) {
      if (sections) swapCartSections(sections);
      openCartDrawer(trigger || document.querySelector('[data-cart-open]'));
    }
  };

  function onCartAddSubmit(event, form) {
    if (event.defaultPrevented) return; /* bundle.js already owns this submit (2/3-tier pick) */
    event.preventDefault();

    var cartRoot = document.querySelector('[data-cart-drawer]');
    var addUrl = (cartRoot && cartRoot.getAttribute('data-add-url')) || form.getAttribute('action');
    var button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    var formData = new FormData(form);
    formData.append('sections', 'cart-drawer,header');
    formData.append('sections_url', window.location.pathname);

    fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: formData })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        unlockButton(button);
        if (result.ok) {
          swapCartSections(result.data.sections || {});
          openCartDrawer(button || form);
        } else {
          /* A real failure (sold out between page load and click, etc.) — let the browser do
             what it would have done without this script rather than trap the shopper. */
          form.submit();
        }
      })
      .catch(function () {
        unlockButton(button);
        form.submit();
      });
  }

  function unlockButton(button) {
    if (!button) return;
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }

  function onQtyButton(button) {
    var line = button.closest('[data-cart-line]');
    if (!line) return;
    var qty = parseInt(button.getAttribute('data-cart-qty'), 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    changeCartLine(line.getAttribute('data-cart-line'), qty);
  }

  function changeCartLine(key, qty) {
    var content = document.querySelector('[data-cart-drawer-content]');
    var cartRoot = document.querySelector('[data-cart-drawer]');
    var changeUrl = (cartRoot && cartRoot.getAttribute('data-change-url')) || '/cart/change';
    if (content) {
      content.setAttribute('aria-busy', 'true');
      content.classList.add('is-pending');
    }
    fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, sections: 'cart-drawer,header', sections_url: window.location.pathname })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        settlePending(content);
        if (data && data.sections) {
          swapCartSections(data.sections);
        } else {
          showCartError();
        }
      })
      .catch(function () {
        settlePending(content);
        showCartError();
      });
  }

  function settlePending(content) {
    if (!content) return;
    content.removeAttribute('aria-busy');
    content.classList.remove('is-pending');
  }

  function showCartError() {
    var errorEl = document.querySelector('[data-cart-error]');
    var cartRoot = document.querySelector('[data-cart-drawer]');
    if (!errorEl || !cartRoot) return;
    errorEl.textContent = cartRoot.getAttribute('data-error') || '';
    errorEl.hidden = false;
  }

  function swapCartSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      var drawerDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var freshContent = drawerDoc.querySelector('[data-cart-drawer-content]');
      var liveContent = document.querySelector('[data-cart-drawer-content]');
      if (freshContent && liveContent) liveContent.innerHTML = freshContent.innerHTML;
    }
    if (sections.header) {
      var headerDoc = new DOMParser().parseFromString(sections.header, 'text/html');
      var freshCart = headerDoc.querySelector('.header__link--cart');
      var liveCart = document.querySelector('.header__link--cart');
      /* Content-only update, not replaceWith: the live anchor stays the SAME node, which is what
         lets its aria-live="polite" (sections/header.liquid) announce the new count. */
      if (freshCart && liveCart) {
        liveCart.setAttribute('aria-label', freshCart.getAttribute('aria-label'));
        liveCart.innerHTML = freshCart.innerHTML;
      }
    }
  }

  /* Hook for assets/bundle.js — see the file header comment. */
  window.CartDrawer = {
    swapAndOpen: function (sections, opener) {
      swapCartSections(sections || {});
      openCartDrawer(opener);
    }
  };

  /* ---------- search overlay ---------- */

  function openSearchOverlay(trigger) {
    var root = document.querySelector('[data-search-overlay]');
    if (!root) return;
    openDialog(root, trigger);
    var input = root.querySelector('[data-search-input]');
    if (input) input.focus();
  }

  function queueSearch(rawValue) {
    clearTimeout(searchDebounce);
    var value = rawValue.trim();
    if (value.length < 2) {
      if (searchAbort) {
        searchAbort.abort();
        searchAbort = null;
      }
      var results = document.querySelector('[data-search-results]');
      if (results) {
        results.innerHTML = '';
        results.removeAttribute('aria-busy');
      }
      return;
    }
    searchDebounce = setTimeout(function () {
      runSearch(value);
    }, 250);
  }

  function onSearchSubmit(event, form) {
    /* Enter stays in the overlay (as in the operator's earlier store) rather than navigating to /search:
       it reuses runSearch as-is, which is fewer lines than adding a second, navigating path. */
    event.preventDefault();
    clearTimeout(searchDebounce);
    var input = form.querySelector('[data-search-input]');
    runSearch(input ? input.value.trim() : '');
  }

  function runSearch(query) {
    if (!query || query.length < 2) return;
    var root = document.querySelector('[data-search-overlay]');
    var results = document.querySelector('[data-search-results]');
    if (!root || !results) return;
    var suggestUrl = root.getAttribute('data-suggest-url') || '/search/suggest';
    if (searchAbort) searchAbort.abort();
    searchAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    results.setAttribute('aria-busy', 'true');

    var url = suggestUrl + '?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=8&section_id=search-overlay';

    fetch(url, searchAbort ? { signal: searchAbort.signal } : {})
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-search-results]');
        results.innerHTML = fresh ? fresh.innerHTML : '';
        results.removeAttribute('aria-busy');
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        results.removeAttribute('aria-busy');
      });
  }
})();
